// src/hooks/useProjectPermissions.js
import { useMemo } from 'react';
import useAuthStore from '../stores/authStore';
import useProjectStore from '../stores/projectStore';

/**
 * Custom hook for unified project permission management
 * 
 * Combines system roles, project team permissions, and resource-specific permissions
 * into a single cohesive permission model
 * 
 * @param {string} projectId - The ID of the project to check permissions for
 * @returns {Object} Object containing various permission flags
 */
const useProjectPermissions = (projectId) => {
  const { user, hasPermission } = useAuthStore();
  const { projects } = useProjectStore();
  
  // Memoize permissions to prevent unnecessary recalculations
  return useMemo(() => {
    if (!user || !projectId) {
      return {
        canViewProject: false,
        canEditProject: false,
        canViewTasks: false,
        canCreateTask: false,
        canUpdateTask: false,
        canDeleteTask: false,
        canCreateSection: false,
        canUpdateSection: false,
        canDeleteSection: false,
        canManageTeam: false,
        canViewFiles: false,
        canUploadFiles: false,
        canDeleteFiles: false,
        canComment: false
      };
    }
    
    // Find the project in the store
    const project = Array.isArray(projects) 
      ? projects.find(p => p._id === projectId)
      : null;
    
    // System-level admin check
    const isAdmin = ['superadmin', 'admin', 'agency-admin'].includes(user.role);
    
    // Project team permissions
    const teamMember = project?.team?.find(member => member.user?._id === user.id);
    const teamPermissions = teamMember?.permissions || {};
    
    // Permission precedence logic:
    // 1. Admins can do everything
    // 2. Team permissions come next
    // 3. Feature-specific permissions are checked last
    
    return {
      // Project-level permissions
      canViewProject: isAdmin || !!teamMember || hasPermission('projects', 'read'),
      canEditProject: isAdmin || teamPermissions.canEdit || hasPermission('projects', 'update'),
      
      // Task permissions
      canViewTasks: isAdmin || !!teamMember || hasPermission('tasks', 'read'),
      canCreateTask: isAdmin || teamPermissions.canManageTasks || hasPermission('tasks', 'create'),
      canUpdateTask: isAdmin || teamPermissions.canManageTasks || hasPermission('tasks', 'update'),
      canDeleteTask: isAdmin || teamPermissions.canManageTasks || hasPermission('tasks', 'delete'),
      
      // Section permissions
      canCreateSection: isAdmin || teamPermissions.canEdit || hasPermission('sections', 'create'),
      canUpdateSection: isAdmin || teamPermissions.canEdit || hasPermission('sections', 'update'),
      canDeleteSection: isAdmin || teamPermissions.canEdit || hasPermission('sections', 'delete'),
      
      // Team management
      canManageTeam: isAdmin || teamPermissions.canEdit || hasPermission('team', 'update'),
      
      // File permissions
      canViewFiles: isAdmin || teamPermissions.canViewFiles || hasPermission('tasks', 'read'),
      canUploadFiles: isAdmin || teamPermissions.canEdit || hasPermission('tasks', 'create'),
      canDeleteFiles: isAdmin || teamPermissions.canEdit || hasPermission('files', 'delete'),
      
      // Comment permissions
      canComment: isAdmin || teamPermissions.canComment || hasPermission('comments', 'create')
    };
  }, [user, projectId, projects, hasPermission]);
};

export default useProjectPermissions;